import { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import {
    LOCAL_SERVER,
    MAIN_SERVER,
    TEST_SERVER,
    resolveCurrentServer,
} from '../utils/serverConfig';

const DevTools = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [storedServer, setStoredServer] = useState(localStorage.getItem('currentServer') || '');

    const servers = {
        localhost: LOCAL_SERVER,
        sitiyTest: TEST_SERVER,
        sitiy: MAIN_SERVER,
    };

    const activeServer = useMemo(() => resolveCurrentServer(storedServer), [storedServer]);

    useEffect(() => {
        const syncServerFromStorage = (event) => {
            if (event.key !== 'currentServer') {
                return;
            }

            setStoredServer(event.newValue || '');
        };

        window.addEventListener('storage', syncServerFromStorage);

        return () => {
            window.removeEventListener('storage', syncServerFromStorage);
        };
    }, []);

    const handleCancel = () => {
        localStorage.removeItem('currentServer');
        setStoredServer('');
        window.dispatchEvent(new Event('currentServerChanged'));
    };

    const handleServerSwitch = (serverKey) => {
        const serverUrl = servers[serverKey];
        setStoredServer(serverUrl);
        localStorage.setItem('currentServer', serverUrl);
        window.dispatchEvent(new Event('currentServerChanged'));
    };
    
    return (
        <>
            {activeServer !== MAIN_SERVER && <Banner>{activeServer}</Banner>}

            <Container $isOpen={isOpen}>
                <Tab onClick={() => setIsOpen(!isOpen)}>
                    <TabInner>{isOpen ? '►' : '◄'}</TabInner>
                </Tab>
                
                <Panel>
                    <Header>Dev Tools</Header>
                    <Content>
                        <ButtonsBlock>
                            <Button $active={activeServer === MAIN_SERVER} onClick={() => handleServerSwitch('sitiy')}>Sitiy</Button>
                            <Button $active={activeServer === TEST_SERVER} onClick={() => handleServerSwitch('sitiyTest')}>Sitiy Test</Button>
                            <Button $active={activeServer === LOCAL_SERVER} onClick={() => handleServerSwitch('localhost')}>Localhost</Button>
                            <Button onClick={handleCancel}>Отменить</Button>
                        </ButtonsBlock>
                    </Content>
                </Panel>
            </Container>
        </>
    );
};

export default DevTools;

const slideIn = keyframes`
    from { transform: translate(100%, -50%); }
    to { transform: translate(0, -50%); }
`;

const slideOut = keyframes`
    from { transform: translate(0, -50%); }
    to { transform: translate(calc(100% - 30px), -50%); }
`;

const Banner = styled.div`
    position: fixed;
    top: 98px;
    right: 0;
    z-index: 1000;
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    padding: 0 15px 0 30px;
    background: #2f2f2f;
    clip-path: polygon(0 0, 100% 0, 100% 100%, 20px 100%);
    overflow: hidden;
    color: #5ea4ff;
`;

const Container = styled.div`
    position: fixed;
    top: 50%;
    right: 0;
    animation: ${({ $isOpen }) => ($isOpen ? slideIn : slideOut)} 0.3s ease forwards;
    display: flex;
    z-index: 1000;
    color: #ffffff;
    pointer-events: none;
`;

const Tab = styled.div`
    width: 30px;
    height: 60px;
    background: rgba(195, 195, 195, 0.7);
    clip-path: polygon(0 20%, 100% 0, 100% 100%, 0 80%);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    overflow: hidden;
    pointer-events: all;
    &:hover {
        background: rgba(195, 195, 195, 0.9);
    }
`;

const TabInner = styled.span`
    font-size: 14px;
    margin-right: 5px;
`;

const Panel = styled.div`
    width: 400px;
    background: rgba(0, 0, 0, 0.85);
    box-shadow: -2px 0 10px rgba(0, 0, 0, 0.3);
    pointer-events: all;
    @media (max-width: 500px) {
        width: 320px;
    }
`;

const Header = styled.div`
    padding: 10px 15px;
    background: rgba(0, 0, 0, 0.9);
    font-weight: bold;
    border-bottom: 1px solid #333;
`;

const Content = styled.div`
    padding: 15px;
    max-height: 70vh;
    overflow-y: auto;
`;

const ButtonsBlock = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 10px;
`;

const Button = styled.button`
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    background: ${({ $active }) => ($active ? '#ea394b' : '#333')};
    color: #fff;
    border: none;
    padding: 10px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-weight: ${({ $active }) => ($active ? '600' : '400')};
    box-shadow: ${({ $active }) => ($active ? 'inset 0 -2px 0 rgba(0,0,0,0.15)' : 'none')};
    &:hover {
        background: ${({ $active }) => ($active ? '#d83445' : '#444')};
    }
    &:disabled {
        background: #555;
        cursor: not-allowed;
    }
    &:active {
        background: ${({ $active }) => ($active ? '#bf2e3a' : '#666')};
    }
    &:focus {
        outline: none;
    }
    &:focus-visible {
        outline: 2px solid #fff;
    }
`;
